/**
 * @file: OrderInfoUpdatedBody.tsx
 *
 * `OrgOrderInfoUpdated` — staff edited an order; renders the diff as
 * a per-field "before → after" table with no-op rows filtered out
 * (producers should already strip these, but a stray pass-through
 * shouldn't show `空 → 空` / `Tesla → Tesla`).
 */

import { Typography } from '@euler/app/components/typography';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgOrderInfoUpdatedFeedPayload } from '@euler/model/feed';
import { FC } from 'react';
import { View } from 'react-native';
import { orderInfoFieldLabel, renderChangedValue } from './common/labels';
import {
  BodyText,
  TitleText,
  labelColor,
  valueColor,
} from './common/primitives';

export const OrderInfoUpdatedBody: FC<{
  p: OrgOrderInfoUpdatedFeedPayload;
}> = ({ p }) => {
  const { rpx } = useResponsiveContext();
  const entries = Object.entries(p.changes).filter(
    ([, change]) => change.before !== change.after,
  );
  return (
    <View>
      <TitleText>工单信息已更新</TitleText>
      <BodyText mt={4} color="#6b7280">
        共 {entries.length} 项变更
      </BodyText>
      {entries.length > 0 ? (
        <View
          css={`
            margin-top: ${rpx(8)}px;
          `}
        >
          {entries.map(([key, change], idx) => (
            <View
              key={key}
              css={`
                padding: ${rpx(6)}px 0;
                ${idx > 0
                  ? `border-top-width: 1px; border-top-color: #e5e7eb;`
                  : ''}
              `}
            >
              <Typography.Text size={rpx(11)} color={labelColor}>
                {orderInfoFieldLabel(key)}
              </Typography.Text>
              <View
                css={`
                  flex-direction: row;
                  align-items: center;
                  margin-top: ${rpx(2)}px;
                  flex-wrap: wrap;
                `}
              >
                <Typography.Text
                  size={rpx(13)}
                  color="#9ca3af"
                  decorationLine="line-through"
                >
                  {renderChangedValue(change.before)}
                </Typography.Text>
                <Icon
                  name="arrow-thin-right"
                  size={rpx(11)}
                  color="#9ca3af"
                  style={{ marginHorizontal: rpx(6) }}
                />
                <Typography.Text size={rpx(13)} color={valueColor} medium>
                  {renderChangedValue(change.after)}
                </Typography.Text>
              </View>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
};
